import React, { createContext, useEffect, useRef, useState } from "react";
import { ConfirmationResult } from "firebase/auth";
import { User, AuthState } from "@/types/auth";
import authService, {
  syncUserStrategiesFromProfile,
  setUserStrategiesCache,
} from "@/services/authService";
import { fyersService } from "@/services/fyersService";
import { ensureFyersCredentials } from "@/features/fyers/fyersCredentials";
import { storage } from "@/utils/helpers";
import { debugFirebaseConfig } from "@/utils/firebaseDebug";
import {
  validatePhoneNumberFormat,
  formatPhoneNumber,
  isTestNumber,
  showAvailableTestNumbers,
} from "@/utils/phoneAuthHelpers";
import { buildTempUser } from "@/features/auth/buildTempUser";
import { setOnboardingProgress } from "@/features/onboarding/onboardingStorage";
import { clearUserScopedCaches } from "@/features/auth/sessionCache";
import {
  fetchOnboardingStatus,
  onboardingStatusKey,
} from "@/features/onboarding/onboardingGuard";
import { fyersUserByUidKey } from "@/features/portfolio/portfolioQueries";
import { queryClient } from "@/lib/queryClient";
import toast from "react-hot-toast";
import {
  signInWithGoogle,
  sendEmailOTP,
  verifyEmailOTP,
  sendPhoneOTP,
  getCurrentUser,
  signOut,
  onAuthStateChanged,
  loginWithEmail,
  signupWithEmail,
  resetPassword,
} from "@/lib/firebase";
import { SyncUserResult } from "@/services/authService";

export function seedCachesFromBootstrapPayload(firebaseUid: string, syncResponse: SyncUserResult, userData: User) {
  if (syncResponse.onboarding) {
    queryClient.setQueryData(
      onboardingStatusKey(firebaseUid),
      {
        hasStrategySubscription: syncResponse.onboarding.has_strategy_subscription,
        hasBrokerCredentials: syncResponse.onboarding.has_broker_credentials,
      }
    );
    if (syncResponse.onboarding.has_broker_credentials) {
      setOnboardingProgress("completed");
    }
    const onboardingStrategies = syncResponse.onboarding.strategies ?? [];
    if (onboardingStrategies.length > 0) {
      setUserStrategiesCache(firebaseUid, onboardingStrategies);
    } else {
      syncUserStrategiesFromProfile(firebaseUid, userData);
    }
  } else {
    syncUserStrategiesFromProfile(firebaseUid, userData);
  }

  if (syncResponse.fyersUser?.client_id) {
    queryClient.setQueryData(
      fyersUserByUidKey(firebaseUid),
      {
        success: true,
        message: "Fyers connection found",
        firebase_uid: firebaseUid,
        fyers_user: syncResponse.fyersUser,
      }
    );
  }

  if (syncResponse.connections) {
    queryClient.setQueryData(
      ["brokers", "connections", firebaseUid],
      syncResponse.connections
    );
  }

  if (syncResponse.allowedBrokers) {
    queryClient.setQueryData(
      ["brokers", "allowed", firebaseUid],
      syncResponse.allowedBrokers
    );
  }

  // Seed broker-check to avoid /brokers/check waterfall
  queryClient.setQueryData(["brokers", "check", firebaseUid], {
    success: true,
    has_credentials: syncResponse.onboarding?.has_broker_credentials ?? false,
    allowed_brokers: syncResponse.allowedBrokers ?? [],
    connections: syncResponse.connections ?? [],
  });

  // Seed profile completeness to avoid /profile-completeness waterfall
  const isProfileComplete = !!(userData.name && userData.email && userData.phone && userData.dob && userData.panCard);
  queryClient.setQueryData(['auth', 'profile-completeness', firebaseUid], {
    success: true,
    firebase_uid: firebaseUid,
    user_found: true,
    profile_complete: isProfileComplete,
    missing_fields: [], // Seeded placeholder
    items: [],
  });
}

export interface AuthContextType extends AuthState {
  // Firebase authentication methods
  loginWithGoogle: () => Promise<{ user: User }>;
  loginWithEmailPassword: (email: string, password: string) => Promise<{ user: User }>;
  signupWithEmailPassword: (email: string, password: string, name?: string) => Promise<{ user: User }>;
  sendPasswordReset: (email: string) => Promise<void>;
  
  // Email OTP authentication
  sendEmailOTP: (email: string) => Promise<boolean>;
  verifyEmailOTP: (email: string, otp: string) => Promise<{ user: User }>;
  
  // User data management
  completeRegistration: (userData: CompleteRegistrationData) => Promise<{ user: User }>;
  updateUserProfile: (userData: Partial<User>) => Promise<{ user: User }>;
  
  // Fyers integration
  checkPhoneInFyers: (phoneNumber: string) => Promise<{ userExists: boolean; client_id?: string; firebase_uid?: string }>;
  
  // Common methods
  logout: (silent?: boolean) => Promise<void>;
}

interface CompleteRegistrationData {
  phone?: string;
  email?: string;
  name?: string;
  dob?: string;
  panCard?: string;
  aadharCard?: string;
  authMethod: 'google' | 'phone';
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

import { useSessionTimeout } from "@/hooks/useSessionTimeout";

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true, // Start with loading to check Firebase auth state
    isAuthenticated: false,
    requires2FA: false,
    token: null, // We don't use tokens with Firebase-only auth
  });
  const lastFirebaseUidRef = useRef<string | null>(null);

  // Apply session timeout for VAPT compliance (15 minutes idle timeout)
  useSessionTimeout(state.isAuthenticated, async () => {
    try {
      lastFirebaseUidRef.current = null;
      storage.remove('auth_user');
      clearUserScopedCaches();
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      try {
        await signOut();
      } catch (e) {}
      toast.error('Session expired due to inactivity');
    } catch (error) {
      console.error(error);
    }
  });

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        const cachedUser = storage.get("auth_user") as User | null;
        const switchedAccount =
          (lastFirebaseUidRef.current &&
            lastFirebaseUidRef.current !== firebaseUser.uid) ||
          (cachedUser?.firebase_uid &&
            cachedUser.firebase_uid !== firebaseUser.uid);

        if (switchedAccount) {
          clearUserScopedCaches();
        }
        lastFirebaseUidRef.current = firebaseUser.uid;

        const hasFreshCache =
          cachedUser?.firebase_uid === firebaseUser.uid;

        // Returning user: trust the local cache and skip the per-load sync.
        // Profile changes go through completeRegistration/updateUserProfile
        // (which sync explicitly) and route decisions use the onboarding-status
        // query, so we no longer POST /auth/sync-user on every page load.
        if (hasFreshCache && cachedUser) {
          setState(prev => ({ ...prev, isLoading: true }));
          try {
            const status = await fetchOnboardingStatus(cachedUser, true);
            if (status.userFound === false) {
              storage.remove("auth_user");
              clearUserScopedCaches();
              await signOut();
              setState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                token: null,
              });
              return;
            }
            const bootstrapResult = await authService.fetchBootstrapData(cachedUser.firebase_uid);
            if (bootstrapResult.success) {
              seedCachesFromBootstrapPayload(cachedUser.firebase_uid, bootstrapResult, cachedUser);
            }
          } catch {
            // Route guard will fall back to local progress if the refresh fails.
          }
          const requires2FA = !sessionStorage.getItem('2fa_verified');
          setState(prev => ({
            ...prev,
            user: cachedUser,
            isAuthenticated: !requires2FA,
            requires2FA: requires2FA,
            isLoading: false,
            token: null,
          }));
          return;
        }

        // First login (or new device): create/link the account and load it.
        setState(prev => ({ ...prev, isLoading: true }));

        try {
          const authMethod = firebaseUser.providerData[0]?.providerId === "password"
            ? "email"
            : (firebaseUser.phoneNumber ? "phone" : "google");

          const syncResponse = await authService.syncUserData({
            firebase_uid: firebaseUser.uid,
            email: firebaseUser.email || "",
            name: firebaseUser.displayName || "User",
            phone: firebaseUser.phoneNumber || undefined,
            avatar: firebaseUser.photoURL || undefined,
            auth_method: authMethod,
          });

          let userData: User | undefined;
          if (syncResponse.success && syncResponse.data) {
            userData = syncResponse.data;
          } else {
            const response = await authService.getUserData(firebaseUser.uid);
            if (response.success && response.data) {
              userData = response.data;
            }
          }

          if (userData) {
            const requires2FA = !sessionStorage.getItem('2fa_verified');
            setState(prev => ({
              ...prev,
              user: userData,
              isAuthenticated: !requires2FA,
              requires2FA: requires2FA,
              isLoading: false,
              token: null,
            }));
            storage.set("auth_user", userData);

            // Seed caches from the bundled bootstrap data so the route guard
            // and the dashboard read from cache instead of firing their own first-login round-trips.
            seedCachesFromBootstrapPayload(userData.firebase_uid, syncResponse, userData);
            
            // Use the broker credentials check from the sync response onboarding payload
            if (
              userData.phone &&
              userData.email &&
              userData.name
            ) {
              if (syncResponse.onboarding?.has_broker_credentials) {
                setOnboardingProgress("completed");
              } else {
                const progress = localStorage.getItem("onboarding_progress");
                if (progress === "completed") {
                  localStorage.setItem(
                    "onboarding_progress",
                    "subscription_completed"
                  );
                }
              }
            }

          } else {
            // User doesn't exist in database or has incomplete profile
            // We wait for the user to provide their phone number on the verification page 
            // before trying to link with existing records by phone.
            // (Previously attempted based on email here, now removed as per user request)
            
            // Fallback: Create temporary user object for login flow handling
            const tempUser = buildTempUser(firebaseUser);
            
            const requires2FA = !sessionStorage.getItem('2fa_verified');
            setState(prev => ({
              ...prev,
              user: tempUser,
              isAuthenticated: !requires2FA,
              requires2FA: requires2FA,
              isLoading: false,
              token: null,
            }));
            
            if (firebaseUser.phoneNumber) {
              storage.set('auth_user', tempUser);
            }
          }
        } catch (error) {
          console.error('Error checking user data:', error);
          
          // If we already have user state and just completed registration, don't override it due to API errors
          if (state.isAuthenticated && state.user) {
            console.log('Preserving authenticated state despite API error');
            setState(prev => ({ ...prev, isLoading: false }));
            return;
          }
          
          // Create temporary user object for incomplete profiles
          const tempUser: User = {
            id: firebaseUser.uid,
            firebase_uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || 'User',
            phone: firebaseUser.phoneNumber || undefined,
            avatar: firebaseUser.photoURL || undefined,
            isVerified: firebaseUser.emailVerified,
            dob: undefined,
            panCard: undefined,
            aadharCard: undefined,
            selectedStrategy: undefined,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
            const requires2FA = !sessionStorage.getItem('2fa_verified');
            setState(prev => ({
              ...prev,
              user: tempUser,
              // Don't override authenticated state if user is already authenticated (just completed registration)
              isAuthenticated: !requires2FA,
              requires2FA: requires2FA,
              isLoading: false,
              token: null,
            }));
          
          // Store the temp user for navigation purposes if authenticated
          if (firebaseUser.phoneNumber) {
            storage.set('auth_user', tempUser);
          }
        }
      } else {
        // No Firebase user
        lastFirebaseUidRef.current = null;
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          token: null,
        });
        storage.remove('auth_user');
      }
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Google Sign In Flow
  const handleLoginWithGoogle = async (): Promise<{ user: User }> => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const result = await signInWithGoogle();
      
      if (!result.user) {
        throw new Error('Google sign-in failed');
      }

      // Create a temporary user object to return
      const tempUser = buildTempUser(result.user);
      toast.success('Google sign-in successful!');
      
      return { user: tempUser };
    } catch (error: unknown) {
      setState(prev => ({ ...prev, isLoading: false }));
      const errorMessage = error instanceof Error ? error.message : 'Google sign-in failed';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Send Email OTP (6-digit)
  const handleSendEmailOTP = async (email: string): Promise<boolean> => {
    try {
      const result = await authService.sendEmailOtp(email);
      toast.success('Verification code sent to your email!');
      return result;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send verification code';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Email and Password Login Flow
  const handleLoginWithEmailPassword = async (email: string, password: string): Promise<{ user: User }> => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const result = await loginWithEmail(email, password);
      
      if (!result.user) {
        throw new Error('Email login failed');
      }

      const tempUser = buildTempUser(result.user);
      toast.success('Sign in successful!');
      return { user: tempUser };
    } catch (error: unknown) {
      setState(prev => ({ ...prev, isLoading: false }));
      if (error instanceof Error && (error.message === 'user-not-found' || error.message === 'invalid-credential')) {
        throw error;
      }
      const errorMessage = error instanceof Error ? error.message : 'Email sign-in failed. Please check your credentials.';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Email and Password Signup Flow
  const handleSignupWithEmailPassword = async (email: string, password: string, name?: string): Promise<{ user: User }> => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const result = await signupWithEmail(email, password, name);
      
      if (!result.user) {
        throw new Error('Email signup failed');
      }

      const tempUser = buildTempUser(result.user);
      toast.success('Sign up successful!');
      return { user: tempUser };
    } catch (error: unknown) {
      setState(prev => ({ ...prev, isLoading: false }));
      if (error instanceof Error && error.message === 'email-already-in-use') {
        throw error;
      }
      const errorMessage = error instanceof Error ? error.message : 'Email sign-up failed. Please try again.';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Password Reset Flow
  const handleSendPasswordReset = async (email: string): Promise<void> => {
    try {
      await resetPassword(email);
      toast.success('Password reset email sent! Please check your inbox.');
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'user-not-found') {
        toast.error('No account found with this email address.');
        throw error;
      }
      toast.error('Failed to send reset email. Please try again.');
      throw error;
    }
  };

  // Verify Email OTP (6-digit)
  const handleVerifyEmailOTP = async (email: string, otp: string): Promise<{ user: User }> => {
    try {
      const result = await authService.verifyEmailOtp(email, otp);
      
      if (!result.success) {
        throw new Error(result.message || 'Invalid verification code');
      }

      // Mark 2FA as verified for this session
      sessionStorage.setItem('2fa_verified', 'true');

      toast.success('Email verification successful!');
      
      const currentUser = state.user || getCurrentUser();
      if (!currentUser) {
        console.warn('No user in state, but OTP verified successfully');
      }

      // Update state to authenticated
      setState(prev => ({ ...prev, isAuthenticated: true, requires2FA: false }));
      
      return { user: currentUser };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Email verification failed';
      throw new Error(errorMessage);
    }
  };

  // Complete Registration
  const handleCompleteRegistration = async (userData: CompleteRegistrationData): Promise<{ user: User }> => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      // For mock system, try to get Firebase user but don't require it
      const firebaseUser = getCurrentUser();

      // Sync user data to backend database
      const syncData = {
        firebase_uid:
          firebaseUser?.uid ||
          (import.meta.env.DEV ? `dev_user_${Date.now()}` : ""),
        email: userData.email || firebaseUser?.email || '',
        name: userData.name || firebaseUser?.displayName || 'User',
        phone: userData.phone,
        avatar: firebaseUser?.photoURL || undefined,
        dob: userData.dob,
        panCard: userData.panCard,  // This will be sent as camelCase
        aadharCard: userData.aadharCard,  // This will be sent as camelCase
        auth_method: userData.authMethod
      };

      console.log('Syncing user data to backend:', syncData);
      const response = await authService.syncUserData(syncData);
      
      if (response.success && response.data) {
        const nextUser = response.data;
        setState(prev => ({
          ...prev,
          user: nextUser,
          isAuthenticated: true,
          isLoading: false,
        }));
        
        storage.set('auth_user', nextUser);
        seedCachesFromBootstrapPayload(nextUser.firebase_uid, response, nextUser);
        
        return { user: nextUser };
      } else {
        throw new Error(response.error || 'Registration failed');
      }
      
    } catch (error: unknown) {
      setState(prev => ({ ...prev, isLoading: false }));
      const errorMessage = error instanceof Error ? error.message : 'Registration completion failed';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Update User Profile (local page spinners handle UX; avoid global isLoading so /confirm state is not unmounted)
  const handleUpdateUserProfile = async (updateData: Partial<User>): Promise<{ user: User }> => {
    try {
      const firebaseUser = getCurrentUser();
      
      if (!state.user) {
        throw new Error('No authenticated user found');
      }

      // For mock system, just sync all user data to backend
      const syncData = {
        firebase_uid: firebaseUser?.uid || state.user.firebase_uid || state.user.id,
        email: updateData.email || firebaseUser?.email || state.user.email,
        name: updateData.name || state.user.name,
        phone: updateData.phone || state.user.phone,
        avatar: updateData.avatar || state.user.avatar,
        dob: updateData.dob || state.user.dob,
        panCard: updateData.panCard || state.user.panCard,
        aadharCard: updateData.aadharCard || state.user.aadharCard,
        selected_strategy: updateData.selectedStrategy || state.user.selectedStrategy,
        broker: updateData.broker !== undefined ? updateData.broker : state.user.broker,
        auth_method: 'google' as const
      };

      const response = await authService.syncUserData(syncData);
      
      if (response.success && response.data) {
        const nextUser = response.data;
        setState(prev => ({
          ...prev,
          user: nextUser,
          isAuthenticated: true,
        }));
        
        storage.set('auth_user', nextUser);
        seedCachesFromBootstrapPayload(nextUser.firebase_uid, response, nextUser);
        
        return { user: nextUser };
      } else {
        throw new Error(response.error || 'Profile update failed');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Check if phone number exists in Fyers
  const handleCheckPhoneInFyers = async (phoneNumber: string): Promise<{ userExists: boolean; client_id?: string; firebase_uid?: string }> => {
    try {
      console.log('Checking phone number in Fyers:', phoneNumber);
      const fyersCheck = await fyersService.checkUserByPhone(phoneNumber);
      
      return {
        userExists: fyersCheck.userExists,
        client_id: fyersCheck.client_id,
        firebase_uid: fyersCheck.firebase_uid
      };
    } catch (error) {
      console.error('Error checking phone in Fyers:', error);
      return {
        userExists: false
      };
    }
  };



  // Logout
  const handleLogout = async (silent = false) => {
    try {
      lastFirebaseUidRef.current = null;
      storage.remove('auth_user');
      clearUserScopedCaches();
      sessionStorage.removeItem('2fa_verified');
      
      // Clear state immediately
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      
      // Try to sign out from Firebase (may not exist in mock system)
      try {
        await signOut();
      } catch (firebaseError) {
        // Ignore Firebase errors in mock system
        console.log('Firebase signout skipped (mock system)');
      }
      
      if (!silent) toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if Firebase signout fails
      storage.remove('auth_user');
      clearUserScopedCaches();
      
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      
      if (!silent) toast.success('Logged out successfully');
    }
  };

  // Listen for unauthorized events to automatically log out
  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn("Received auth:unauthorized event. Logging out...");
      toast.error("Your session has expired. Please log in again.");
      handleLogout(true);
    };
    
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const value: AuthContextType = {
    ...state,
    loginWithGoogle: handleLoginWithGoogle,
    loginWithEmailPassword: handleLoginWithEmailPassword,
    signupWithEmailPassword: handleSignupWithEmailPassword,
    sendPasswordReset: handleSendPasswordReset,
    sendEmailOTP: handleSendEmailOTP,
    verifyEmailOTP: handleVerifyEmailOTP,
    completeRegistration: handleCompleteRegistration,
    updateUserProfile: handleUpdateUserProfile,
    checkPhoneInFyers: handleCheckPhoneInFyers,
    logout: handleLogout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
